import { useMemo } from 'react';

import useAuth from '@/api/auth/useAuth';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import { useIsModuleEnabled } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import { useRoutePermissions } from '@/hooks/authz/useRoutePermissions';
import { routeTree } from 'src/routes';

import type { AccessContext } from '@/routing/access-meta';

export function useAccessContext(): AccessContext {
  const { isModuleEnabled } = useIsModuleEnabled();
  const { currentUser } = useAuth();
  const { viewMode } = useViewMode();

  const { permissions, isLoading: permissionsLoading } =
    useRoutePermissions(routeTree);

  // Feature flags used to hide routes today
  const { enabled: showEmailPoMetricsEnabled } = useFeatureFlag(
    'show-email-po-metrics-enabled',
    false,
  );
  const { enabled: agentMonitorEnabled } = useFeatureFlag(
    'agent-monitor-enabled',
    false,
  );
  const { enabled: itemMasterUiEnabled } = useFeatureFlag(
    'item-master-ui-enabled',
    false,
  );
  const { enabled: imPricelistManagementEnabled } = useFeatureFlag(
    'im-pricelist-management',
    false,
  );

  const featureFlags = useMemo<Record<string, boolean>>(
    () => ({
      'show-email-po-metrics-enabled': showEmailPoMetricsEnabled,
      'agent-monitor-enabled': agentMonitorEnabled,
      'item-master-ui-enabled': itemMasterUiEnabled,
      'im-pricelist-management': imPricelistManagementEnabled,
    }),
    [
      showEmailPoMetricsEnabled,
      agentMonitorEnabled,
      itemMasterUiEnabled,
      imPricelistManagementEnabled,
    ],
  );

  const userIsAdmin = useMemo(
    () => isAdmin(currentUser, viewMode),
    [currentUser, viewMode],
  );

  return {
    isAdmin: userIsAdmin,
    isFeatureEnabled: (flag: string) => featureFlags[flag] ?? false,
    isModuleEnabled,
    permissions,
    permissionsLoading,
  };
}
